import { queryOptions } from "@tanstack/vue-query";
import camelize from "camelcase-keys";
import snakeize from "snakecase-keys";
import { FetchError } from "ofetch";

export const memberQueries = {
  all: () => ["member"] as const,
  donations: (organizationId: MaybeRefOrGetter<number>) =>
    queryOptions({
      queryKey: [...memberQueries.all(), "donations", { organizationId: toValue(organizationId) }] as const,
      queryFn: async () => {
        const client = useApiClient();

        const response = await client<DonationsResponse>(`/member/donations`, {
          query: snakeize({ organizationId: toValue(organizationId) }, { deep: true }),
        });

        return camelize(response, { deep: true });
      },
      staleTime: 1000 * 60 * 60,
    }),
  impactFamily: (organizationId: MaybeRefOrGetter<number>) =>
    queryOptions({
      queryKey: [...memberQueries.all(), "impactFamily", { organizationId: toValue(organizationId) }] as const,
      queryFn: async () => {
        const client = useApiClient();

        const response = await client<ImpactFamilyResponse>(`/member/impactfamily`, {
          query: snakeize({ organizationId: toValue(organizationId) }, { deep: true }),
        });

        return camelize(response, { deep: true });
      },
      staleTime: 1000 * 60 * 60,
    }),
  subscription: (organizationId: MaybeRefOrGetter<number>) =>
    queryOptions({
      queryKey: [...memberQueries.all(), "subscription", { organizationId: toValue(organizationId) }] as const,
      queryFn: async () => {
        const client = useApiClient();

        try {
          const response = await client<SubscriptionResponse>(`/member/subscription`, {
            query: snakeize({ organizationId: toValue(organizationId) }, { deep: true }),
          });

          return camelize(response, { deep: true });
        } catch (error) {
          if (error instanceof FetchError) {
            if (error.status === 404) return null;
          }

          throw error;
        }
      },
      staleTime: 1000 * 60 * 60,
    }),
  perks: (organizationId: MaybeRefOrGetter<number>) =>
    queryOptions({
      queryKey: [...memberQueries.all(), "perks", { organizationId: toValue(organizationId) }] as const,
      queryFn: async () => {
        const client = useApiClient();

        const response = await client<PerksResponse>("/member/perks", {
          query: snakeize({ organizationId: toValue(organizationId) }),
        });

        return camelize(response, { deep: true });
      },
    }),

  tier: (organizationId: MaybeRefOrGetter<number>) =>
    queryOptions({
      queryKey: [...memberQueries.all(), "tier", { organizationId: toValue(organizationId) }] as const,
      queryFn: async () => {
        const client = useApiClient();

        const response = await client<TierResponse>("/member/tier", {
          query: snakeize({ organizationId: toValue(organizationId) }),
        });

        return response ? camelize(response, { deep: true }) : null;
      },
    }),
  vote: (pollId: MaybeRefOrGetter<number>) =>
    queryOptions({
      queryKey: [...memberQueries.all(), "vote", { pollId: toValue(pollId) }] as const,
      queryFn: async () => {
        const client = useApiClient();

        const response = await client<{ optionId: number | null }>(`/polls/${toValue(pollId)}/vote`);

        return camelize(response, { deep: true });
      },
      staleTime: 1000 * 60 * 60,
    }),
};

type DonationsResponse = Array<{
  id: number;
  amount: number;
  transaction_id: string;
  receipt_id: string;
  message: string | null;
  created_at: string;
  organization_id: number;
  member_id: number;
  donation: number;
  subscription_id: number | null;
  tip: number;
  organization_amount: number;
  stripe_fee: number;
  ddc_fee: number;
  status: "paid" | "pending" | "failed";
}>;

type ImpactFamilyResponseTree = {
  id: number;
  name: string;
  points: number;
  avatar_url: string | null;
  children: Array<ImpactFamilyResponseTree>;
  total_donations: number;
  total_tips: number;
};

type ImpactFamilyResponse = {
  tree: ImpactFamilyResponseTree;
  collective_impact: {
    total_donations: number;
    total_tips: number;
  };
};

type SubscriptionResponse = {
  id: number;
  amount: number;
  stripe_fee: number;
  message: number | null;
  status: "active" | "past_due" | "canceled" | "pending_first_payment" | "not_started";
  member_id: number;
  donation: number;
  tip: number;
  ddc_fee: number;
  organization_id: number;
  total_amount: number;
  type: "organization" | "ddc";
};

type PerksResponse = Array<{
  id: number;
  tier_id: number;
  name: string;
  description: string | null;
  deliver_url: string | null;
  created_at: string;
  updated_at: string;
}>;

type TierResponse = {
  id: number;
  organization_id: number;
  name: string;
  description: string;
  icon: string;
  price: number;
  published: boolean;
  createdAt: string;
  updatedAt: string;
} | null;
